// Capability-typed description of where an EditReference's options come
// from. The control's behavior (client-side filter vs debounced
// server-side search, eager load vs none) is driven entirely by which
// kind is supplied — there is one rendering path internally.
export type ReferenceSource<T> =
  // Already in memory; filtered client-side. No loading state.
  | { readonly kind: 'static'; readonly items: readonly T[] }
  // Loaded once on mount, then filtered client-side.
  | { readonly kind: 'eager'; readonly load: () => Promise<readonly T[]> }
  // Debounced server-side search. The initial fetch (size `threshold`)
  // both learns the total and, when the universe is small enough,
  // collapses to client-side filtering. `resolve` hydrates a labelled
  // item from a key when the controlled value carries only its id
  // (sentinel). `interimValue` builds a stand-in item to show while
  // `resolve` is in flight, so a selected-but-unresolved value reads as
  // set rather than blank. Both are required for the same reason: the
  // source can't know whether a given consumer ever supplies key-only
  // values, so it must always be able to render one.
  | {
      readonly kind: 'search';
      readonly search: (query: string, limit: number) => Promise<{ readonly items: readonly T[]; readonly total: number }>;
      readonly resolve: (key: string | number) => Promise<T | null>;
      readonly interimValue: (key: string | number) => T;
      readonly threshold?: number;
    };

// What the EditReference `source` prop accepts. The bare-array and
// bare-loader forms are the pre-#842 calling convention; they normalize
// to `static` / `eager` so existing callers keep working unchanged.
export type SourceProp<T> = ReferenceSource<T> | readonly T[] | (() => Promise<readonly T[]>);

export const normalizeSource = <T>(source: SourceProp<T>): ReferenceSource<T> => {
  if (typeof source === 'function') return { kind: 'eager', load: source };
  if (Array.isArray(source)) return { kind: 'static', items: source };
  // `Array.isArray` can't narrow `readonly T[]` out of the union (it's typed
  // against mutable `any[]`), so discriminate the object form on `kind`.
  if ('kind' in source) return source;
  return { kind: 'static', items: source };
};
